// Utility functions and global variables
const ON_CHANGE_DEBOUNCE_TIMER = 300;

// Debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

// Shopify routes
window.routes = window.routes || {
  cart_url: '/cart',
  cart_add_url: '/cart/add.js',
  cart_change_url: '/cart/change.js',
  cart_update_url: '/cart/update.js',
  all_products_collection_url: '/collections/all'
};

// Fetch config for AJAX requests
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

// Pub/Sub events system
const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update'
};

function subscribe(eventName, callback) {
  const unsubscribe = () => {
    document.removeEventListener(eventName, callback);
  };
  document.addEventListener(eventName, callback);
  return unsubscribe;
}

function publish(eventName, data) {
  const event = new CustomEvent(eventName, { detail: data });
  document.dispatchEvent(event);
}

// Cart performance measurement
const CartPerformance = {
  measure: (name, fn) => fn(),
  measureFromEvent: (name, event) => {}
};

// Focus trap utility
function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  container.setAttribute('tabindex', '-1');
  elementToFocus.focus();

  function trapFocusHandler(e) {
    if (e.target === container) return;
    
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (e.target === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (e.target === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  document.addEventListener('keydown', trapFocusHandler);
  document.body.classList.add('js-focus-trap');

  function removeTrapFocus() {
    document.removeEventListener('keydown', trapFocusHandler);
    document.body.classList.remove('js-focus-trap');
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// Helper function to get translated string with fallback
function getCartTranslation(path, fallback) {
  if (!window.cartTranslations) return fallback;
  const keys = path.split('.');
  let value = window.cartTranslations;
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) return fallback;
  }
  return value || fallback;
}

// Initialize cart strings - will use translations if available
// This function can be called multiple times to update strings when translations become available
function initCartStrings() {
  // Always update to ensure we have the latest translations
  window.cartStrings = window.cartStrings || {};
  window.cartStrings.error = getCartTranslation('errors.general', 'Something went wrong');
  window.cartStrings.quantityError = getCartTranslation('errors.quantityError', 'You can only add [quantity] of this item to your cart.');
  window.cartStrings.cartError = getCartTranslation('errors.cartError', 'There was an error while updating your cart. Please try again.');
  window.cartStrings.soldOut = window.cartStrings.soldOut || 'Sold out';
  window.cartStrings.unavailable = window.cartStrings.unavailable || 'Unavailable';

  window.quickOrderListStrings = window.quickOrderListStrings || {};
  window.quickOrderListStrings.min_error = getCartTranslation('validation.minError', 'Quantity must be at least [min]');
  window.quickOrderListStrings.max_error = getCartTranslation('validation.maxError', 'Quantity must be at most [max]');
  window.quickOrderListStrings.step_error = getCartTranslation('validation.stepError', 'Quantity must be in increments of [step]');
}

// Initialize immediately, and also re-initialize when translations become available
initCartStrings();

// Re-initialize when translations are loaded (in case cart.js loads before the script tag in cart.liquid)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartStrings);
} else {
  // If DOM is already loaded, try again after a short delay to catch late-loaded translations
  setTimeout(initCartStrings, 100);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      
      const index = this.dataset.index;
      const quantityInput = document.getElementById(`Quantity-${index}`);
      if (quantityInput) {
        quantityInput.value = 0;
        
        // Trigger change event to update cart automatically
        const changeEvent = new Event('change', { bubbles: true });
        quantityInput.dispatchEvent(changeEvent);
      }
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    // Flag to track if this is initial load
    this.isInitialLoad = true;

    const debouncedOnChange = debounce((event) => {
      // Skip if initial load or if event is not from user interaction
      if (this.isInitialLoad) {
        this.isInitialLoad = false;
        return;
      }
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        return this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sections = [];
    
    // Add main cart items if it exists
    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        id: "main-cart-items",
        section: mainCartItems.dataset.id,
        selector: "#main-cart-items .js-contents",
      });
    }
    
    // Add cart footer if it exists
    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        id: "main-cart-footer",
        section: mainCartFooter.dataset.id,
        selector: "#main-cart-footer .js-contents",
      });
    }
    
    // Note: Cart header is updated directly via updateCartHeader() method
    // No need to request it as a section since we have the item count
    
    // Add cart icon bubble if it exists (for header cart)
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (cartIconBubble) {
      sections.push({
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      });
    }
    
    // Add live region if it exists
    const cartLiveRegion = document.getElementById("cart-live-region-text");
    if (cartLiveRegion) {
      sections.push({
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      });
    }
    
    return sections;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // Add timeout to prevent stuck loading state
    const loadingTimeout = setTimeout(() => {
      console.warn("Cart update timeout - disabling loading state");
      this.disableLoading(line);
    }, 5000); // 5 second timeout

    const sectionsToRender = this.getSectionsToRender();
    
    // Deduplicate section IDs to avoid requesting the same section multiple times
    const uniqueSectionIds = [...new Set(sectionsToRender.map((section) => section.section))];

    const body = JSON.stringify({
      line,
      quantity,
      sections: uniqueSectionIds,
      sections_url: window.location.pathname,
    });
    const eventTarget =
      event.currentTarget instanceof CartRemoveButton ? "clear" : "change";

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(
          `${eventTarget}:paint-updated-sections"`,
          () => {
            const quantityElement =
              document.getElementById(`Quantity-${line}`) ||
              document.getElementById(`Drawer-quantity-${line}`);
            const items = document.querySelectorAll(".cart-item");

            if (parsedState.errors) {
              quantityElement.value = quantityElement.getAttribute("value");
              this.updateLiveRegions(line, parsedState.errors);
              return;
            }

            this.classList.toggle("is-empty", parsedState.item_count === 0);
            const cartDrawerWrapper = document.querySelector("cart-drawer");
            const cartFooter = document.getElementById("main-cart-footer");

            if (cartFooter)
              cartFooter.classList.toggle(
                "is-empty",
                parsedState.item_count === 0
              );
            if (cartDrawerWrapper)
              cartDrawerWrapper.classList.toggle(
                "is-empty",
                parsedState.item_count === 0
              );

            // Handle empty cart state dynamically
            if (parsedState.item_count === 0) {
              this.showEmptyCartState();
              this.updateCartHeader(parsedState.item_count);
            } else {
              this.showCartWithItems();
              this.updateCartHeader(parsedState.item_count);
            }

            // Cart header is updated directly via updateCartHeader() above

            this.getSectionsToRender().forEach((section) => {
              const containerElement = document.getElementById(section.id);
              if (!containerElement) return;
              
              // Find the element to replace within the container
              const elementToReplace = containerElement.querySelector('.js-contents') || containerElement;
              
              if (elementToReplace && parsedState.sections[section.section]) {
                // Extract the content from the returned section HTML using the selector
                const newContent = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector
                );
                
                if (newContent) {
                  // Clear any existing content to prevent duplication
                  elementToReplace.innerHTML = newContent;
                }
              }
            });
            
            // Ensure cart state visibility is correct after all updates
            if (parsedState.item_count === 0) {
              this.showEmptyCartState();
            } else {
              this.showCartWithItems();
            }
            const updatedValue = parsedState.items[line - 1]
              ? parsedState.items[line - 1].quantity
              : undefined;
            let message = "";
            if (
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value)
            ) {
              if (typeof updatedValue === "undefined") {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace(
                  "[quantity]",
                  updatedValue
                );
              }
            }
            this.updateLiveRegions(line, message);

            const lineItem =
              document.getElementById(`CartItem-${line}`) ||
              document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(
                    cartDrawerWrapper,
                    lineItem.querySelector(`[name="${name}"]`)
                  )
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(
                cartDrawerWrapper.querySelector(".drawer__inner-empty"),
                cartDrawerWrapper.querySelector("a")
              );
            } else if (
              document.querySelector(".cart-item") &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper,
                document.querySelector(".cart-item__name")
              );
            }
          }
        );

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error("Cart update error:", error);
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) {
          errors.textContent = window.cartStrings?.error || getCartTranslation('errors.errorOccurred', 'An error occurred');
        }
        // Reset quantity on error
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) {
          quantityElement.value = quantityElement.getAttribute("value");
        }
      })
      .finally(() => {
        clearTimeout(loadingTimeout);
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    try {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const element = parsed.querySelector(selector);
      if (!element) {
        console.warn(`Selector "${selector}" not found in section HTML`);
        return null;
      }
      return element.innerHTML;
    } catch (error) {
      console.error('Error parsing section HTML:', error);
      return null;
    }
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.add("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    if (document.activeElement) {
      document.activeElement.blur();
    }
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) {
      mainCartItems.classList.remove("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }

  updateCartHeader(itemCount) {
    const cartHeader = document.querySelector('.cart__header');
    if (!cartHeader) return;
    
    let cartSubtitle = cartHeader.querySelector('.cart__subtitle');
    
    if (itemCount === 0) {
      // Hide or remove subtitle when cart is empty
      if (cartSubtitle) {
        cartSubtitle.style.display = 'none';
      }
    } else {
      // Show and update subtitle when cart has items
      if (!cartSubtitle) {
        // Create subtitle if it doesn't exist
        cartSubtitle = document.createElement('p');
        cartSubtitle.className = 'cart__subtitle';
        const cartTitle = cartHeader.querySelector('.cart__title');
        if (cartTitle && cartTitle.nextSibling) {
          cartHeader.insertBefore(cartSubtitle, cartTitle.nextSibling);
        } else {
          cartHeader.appendChild(cartSubtitle);
        }
      }
      cartSubtitle.style.display = '';
      // Use translations if available, otherwise fallback to English
      const itemText = itemCount === 1 
        ? (window.cartTranslations?.item || 'item')
        : (window.cartTranslations?.items || 'items');
      cartSubtitle.textContent = `${itemCount} ${itemText}`;
    }
  }

  showEmptyCartState() {
    const cartWithItems = document.getElementById("cart-with-items");
    const cartEmptyState = document.getElementById("cart-empty-state");
    const cartContentContainer = document.getElementById("cart-content-container");
    
    // Ensure the container is visible
    if (cartContentContainer) {
      cartContentContainer.style.display = "";
      cartContentContainer.classList.remove("is-hidden");
    }
    
    if (cartWithItems) {
      cartWithItems.classList.add("is-hidden");
    }
    if (cartEmptyState) {
      cartEmptyState.classList.remove("is-hidden");
      // Ensure it's visible (remove any inline styles that might override)
      cartEmptyState.style.display = "";
    } else {
      console.warn("Cart empty state element not found");
    }
  }

  showCartWithItems() {
    const cartWithItems = document.getElementById("cart-with-items");
    const cartEmptyState = document.getElementById("cart-empty-state");
    
    if (cartWithItems) {
      cartWithItems.classList.remove("is-hidden");
      // Ensure it's visible (remove any inline styles that might override)
      cartWithItems.style.display = "";
    }
    if (cartEmptyState) {
      cartEmptyState.classList.add("is-hidden");
    }
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const textarea = this.querySelector('textarea');
        if (textarea) {
          textarea.addEventListener(
            "input",
            debounce((event) => {
              const body = JSON.stringify({ note: event.target.value });
              fetch(`${routes.cart_update_url}`, {
                ...fetchConfig(),
                ...{ body },
              }).then(() =>
                CartPerformance.measureFromEvent("note-update:user-action", event)
              );
            }, ON_CHANGE_DEBOUNCE_TIMER)
          );
        }
      }
    }
  );
}

// Quantity Button Controls
class QuantityButton extends HTMLElement {
  constructor() {
    super();
    
    this.addEventListener("click", (event) => {
      event.preventDefault();
      
      const index = this.dataset.index;
      const quantity = parseInt(this.dataset.quantity);
      const input = document.getElementById(`Quantity-${index}`);
      
      if (input) {
        input.value = Math.max(0, quantity);
        
        // Trigger change event to update cart automatically
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
      }
    });
  }
}

customElements.define("quantity-button", QuantityButton);

// Global cart icon update handler
function updateCartIconBubble(itemCount) {
  const cartIcons = document.querySelectorAll('[id^="cart-icon-bubble"]');
  
  cartIcons.forEach(cartIcon => {
    let countBadge = cartIcon.querySelector('.menu-single-row__cart-count');
    
    if (itemCount > 0) {
      if (!countBadge) {
        countBadge = document.createElement('sup');
        countBadge.className = 'menu-single-row__cart-count';
        const icon = cartIcon.querySelector('[data-lucide]');
        if (icon) {
          cartIcon.insertBefore(countBadge, icon);
        } else {
          cartIcon.insertBefore(countBadge, cartIcon.firstChild);
        }
      }
      countBadge.textContent = itemCount;
    } else if (countBadge) {
      countBadge.remove();
    }
  });
}

// Listen for cart updates from product form
document.addEventListener('cart:updated', (event) => {
  if (event.detail && event.detail.itemCount !== undefined) {
    updateCartIconBubble(event.detail.itemCount);
  }
});

// Listen for cart updates from cart page itself
document.addEventListener(PUB_SUB_EVENTS.cartUpdate, (event) => {
  if (event.detail && event.detail.cartData) {
    updateCartIconBubble(event.detail.cartData.item_count);
  }
});

// Checkout Modal Functionality
class CheckoutModal {
  constructor() {
    this.modal = document.getElementById('checkout-modal');
    this.openButton = document.getElementById('open-checkout-modal');
    this.closeButton = document.getElementById('close-checkout-modal');
    this.cancelButton = document.getElementById('cancel-checkout-modal');
    this.form = document.getElementById('checkout-modal-form');
    this.overlay = this.modal?.querySelector('.checkout-modal__overlay');
    this.messageDiv = document.getElementById('checkout-modal-message');
    
    if (!this.modal || !this.openButton || !this.form) {
      return;
    }
    
    this.init();
  }
  
  init() {
    // Open modal
    this.openButton.addEventListener('click', () => this.open());
    
    // Close modal
    this.closeButton?.addEventListener('click', () => this.close());
    this.cancelButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display !== 'none') {
        this.close();
      }
    });
    
    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }
  
  async open() {
    // Fetch latest cart data to ensure it's up to date
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      const cartData = await response.json();
      this.updateModalWithCartData(cartData);
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
    
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // Focus first input
    const firstInput = this.form.querySelector('input');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
  }
  
  close() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    this.hideMessage();
    
    // Reset form if submission was successful
    if (this.form.dataset.submitted === 'true') {
      this.form.reset();
      this.form.dataset.submitted = 'false';
    }
  }
  
  updateModalWithCartData(cartData) {
    const itemsContainer = document.getElementById('checkout-modal-items');
    const subtotalEl = document.getElementById('checkout-modal-subtotal');
    const totalEl = document.getElementById('checkout-modal-total');
    
    if (!itemsContainer) return;
    
    // Update items
    itemsContainer.innerHTML = '';
    cartData.items.forEach((item, index) => {
      const itemEl = document.createElement('div');
      itemEl.className = 'checkout-modal__item';
      itemEl.dataset.line = index + 1;
      
      const imageUrl = item.image ? item.image : '';
      const imageSrc = imageUrl ? imageUrl.replace(/(\.\w+)(\?.*)?$/, '_80x80$1') : '';
      
      itemEl.innerHTML = `
        ${imageSrc ? `<img src="${imageSrc}" alt="${item.title}" class="checkout-modal__item-image">` : '<div class="checkout-modal__item-image checkout-modal__item-image--placeholder"></div>'}
        <div class="checkout-modal__item-details">
          <p class="checkout-modal__item-title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="checkout-modal__item-variant">${item.variant_title}</p>` : ''}
          <p class="checkout-modal__item-quantity">Qty: ${item.quantity}</p>
        </div>
        <p class="checkout-modal__item-price">${this.formatMoney(item.final_line_price)}</p>
      `;
      
      itemsContainer.appendChild(itemEl);
    });
    
    // Update totals
    if (subtotalEl) {
      subtotalEl.textContent = this.formatMoney(cartData.total_price);
    }
    if (totalEl) {
      totalEl.textContent = this.formatMoney(cartData.total_price);
    }
  }
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    
    const submitButton = document.getElementById('submit-checkout-modal');
    const originalText = submitButton.textContent;
    
    // Disable button and show loading state
    submitButton.disabled = true;
    submitButton.textContent = 'Sending...';
    this.hideMessage();
    
    try {
      // Fetch current cart data
      const cartResponse = await fetch(window.routes.cart_url + '.js');
      const cartData = await cartResponse.json();
      
      // Get form data
      const formData = new FormData(this.form);
      const name = formData.get('contact[name]');
      const email = formData.get('contact[email]');
      
      // Prepare email content for the order details hidden field
      let emailBody = `NEW ORDER REQUEST\n\n`;
      emailBody += `Customer Information:\n`;
      emailBody += `Name: ${name}\n`;
      emailBody += `Email: ${email}\n\n`;
      emailBody += `Order Details:\n`;
      emailBody += `${'='.repeat(50)}\n\n`;
      
      cartData.items.forEach((item, index) => {
        emailBody += `${index + 1}. ${item.product_title}\n`;
        if (item.variant_title && item.variant_title !== 'Default Title') {
          emailBody += `   Variant: ${item.variant_title}\n`;
        }
        emailBody += `   Quantity: ${item.quantity}\n`;
        emailBody += `   Price: ${this.formatMoney(item.final_line_price)}\n\n`;
      });
      
      emailBody += `${'='.repeat(50)}\n`;
      emailBody += `Subtotal: ${this.formatMoney(cartData.total_price)}\n`;
      if (cartData.total_discount > 0) {
        emailBody += `Discount: -${this.formatMoney(cartData.total_discount)}\n`;
      }
      emailBody += `Total: ${this.formatMoney(cartData.total_price)}\n`;
      
      // Update the hidden field with cart data
      const cartDataField = document.getElementById('checkout-cart-data');
      if (cartDataField) {
        cartDataField.value = emailBody;
      }
      
      // Submit the form using XMLHttpRequest to avoid CSP issues
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/contact', true);
      xhr.setRequestHeader('Accept', 'application/javascript');
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          // Success
          this.showMessage('Thank you! Your order request has been submitted successfully. We will contact you shortly.', 'success');
          this.form.dataset.submitted = 'true';
          
          // Close modal after 3 seconds
          setTimeout(() => {
            this.close();
          }, 3000);
        } else {
          throw new Error('Failed to submit form');
        }
        
        // Re-enable button
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      };
      
      xhr.onerror = () => {
        console.error('Error submitting order');
        this.showMessage('Sorry, there was an error submitting your order. Please try again or contact us directly.', 'error');
        
        // Re-enable button
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      };
      
      xhr.send(new FormData(this.form));
      
    } catch (error) {
      console.error('Error submitting order:', error);
      this.showMessage('Sorry, there was an error submitting your order. Please try again or contact us directly.', 'error');
      
      // Re-enable button
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }
  
  showMessage(text, type) {
    if (!this.messageDiv) return;
    
    this.messageDiv.textContent = text;
    this.messageDiv.className = `checkout-modal__message checkout-modal__message--${type}`;
    this.messageDiv.style.display = 'block';
  }
  
  hideMessage() {
    if (!this.messageDiv) return;
    this.messageDiv.style.display = 'none';
  }
}

// Initialize checkout modal when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CheckoutModal();
  });
} else {
  new CheckoutModal();
}
